import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  MessageCircle, 
  Palette, 
  PackageOpen, 
  Factory, 
  Truck,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

gsap.registerPlugin(ScrollTrigger);

const Process = () => {
  const stepsRef = useRef([]);
  const benefitsRef = useRef([]);

  useEffect(() => {
    // Animate process steps
    stepsRef.current.forEach((step, index) => {
      if (step) {
        gsap.from(step, {
          scrollTrigger: {
            trigger: step,
            start: 'top 85%',
          },
          x: -60,
          opacity: 0,
          duration: 0.8,
          delay: index * 0.15,
          ease: 'power3.out'
        });
      }
    });

    // Animate benefits
    benefitsRef.current.forEach((benefit, index) => {
      if (benefit) {
        gsap.from(benefit, {
          scrollTrigger: {
            trigger: benefit,
            start: 'top 85%',
          },
          y: 40,
          opacity: 0,
          duration: 0.6,
          delay: index * 0.1,
          ease: 'power2.out'
        });
      }
    });
  }, []);

  const processSteps = [
    {
      number: '01',
      icon: MessageCircle,
      title: 'Consultation',
      description: 'Understanding the client product requirements, branding goals, and functional needs. We discuss materials, dimensions, printing specifications, and any special considerations.',
      deliverables: ['Requirement documentation', 'Material recommendations', 'Initial cost estimates']
    },
    {
      number: '02',
      icon: Palette,
      title: 'Design Development',
      description: 'Creating packaging concepts that balance visual identity with structural strength. Our design team develops layouts, selects appropriate materials, and ensures compliance with industry standards.',
      deliverables: ['Design mockups', 'Technical drawings', 'Material specifications']
    },
    {
      number: '03',
      icon: PackageOpen,
      title: 'Prototyping',
      description: 'Producing samples to test durability, usability, and aesthetics before final production. Prototypes allow clients to evaluate structure, finish, and practical functionality.',
      deliverables: ['Physical prototypes', 'Testing results', 'Refinement recommendations']
    },
    {
      number: '04',
      icon: Factory,
      title: 'Manufacturing',
      description: 'Executing large-scale production with precision equipment and quality materials. Our manufacturing process includes multiple quality checkpoints to ensure consistency across all units.',
      deliverables: ['Production run', 'Quality inspection reports', 'Batch documentation']
    },
    {
      number: '05',
      icon: Truck,
      title: 'Delivery',
      description: 'Ensuring secure packaging and timely dispatch to client locations. We coordinate logistics, track shipments, and confirm successful delivery to maintain project timelines.',
      deliverables: ['Packaged products', 'Delivery documentation', 'Quality certificates']
    }
  ];

  const benefits = [
    'Clear communication at every stage',
    'Transparent timeline and cost structure',
    'Quality checkpoints throughout production',
    'Single point of contact for the entire process',
    'Flexibility to accommodate changes during early stages',
    'Documentation and samples for approval'
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Our Approach</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
              Our Packaging
              <br />
              <span className="text-gray-600">Process</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              At Sagar Pack, every project follows a structured process designed to ensure accuracy, 
              efficiency, and consistent quality from concept to delivery.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto space-y-16">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={index}
                  ref={el => stepsRef.current[index] = el}
                  className="relative"
                >
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    {/* Number & Icon */}
                    <div className="flex-shrink-0 flex flex-col items-center space-y-4">
                      <div className="w-20 h-20 rounded-lg bg-navy-900 text-white flex items-center justify-center text-2xl font-bold">
                        {step.number}
                      </div>
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                        <IconComponent className="h-8 w-8 text-navy-900" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <Card className="border-2 hover:shadow-lg transition-shadow duration-300">
                        <CardContent className="p-8 space-y-4">
                          <h3 className="text-3xl font-bold text-navy-900">{step.title}</h3>
                          <p className="text-lg text-gray-700 leading-relaxed">{step.description}</p>
                          
                          <div className="pt-4">
                            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">
                              Deliverables
                            </p>
                            <ul className="space-y-2">
                              {step.deliverables.map((deliverable, idx) => (
                                <li key={idx} className="flex items-center text-gray-700">
                                  <CheckCircle className="h-5 w-5 text-navy-900 mr-3 flex-shrink-0" />
                                  {deliverable}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Connector Line (except for last item) */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute left-10 top-20 bottom-0 w-0.5 bg-gray-200 -mb-16"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Benefits */}
      <section className="py-24 bg-navy-900 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Why This Process Works</h2>
              <p className="text-lg text-gray-300">
                A structured approach that prioritizes clarity, quality, and client satisfaction
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  ref={el => benefitsRef.current[index] = el}
                  className="flex items-center space-x-3 p-4 bg-white/5 rounded-lg"
                >
                  <CheckCircle className="h-6 w-6 text-white flex-shrink-0" />
                  <span className="text-gray-100">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Overview */}
      <section className="py-24 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold text-navy-900">Typical Project Timeline</h2>
            <p className="text-lg text-gray-600">
              Most projects are completed within 2-4 weeks, depending on complexity and order quantity
            </p>
            
            <div className="grid md:grid-cols-5 gap-4 pt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-navy-900 mb-2">1-2</div>
                <div className="text-sm text-gray-600">Days<br />Consultation</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-navy-900 mb-2">3-5</div>
                <div className="text-sm text-gray-600">Days<br />Design</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-navy-900 mb-2">3-5</div>
                <div className="text-sm text-gray-600">Days<br />Prototyping</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-navy-900 mb-2">7-14</div>
                <div className="text-sm text-gray-600">Days<br />Manufacturing</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-navy-900 mb-2">2-3</div>
                <div className="text-sm text-gray-600">Days<br />Delivery</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold text-navy-900 mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get in touch to discuss your packaging requirements and begin the consultation process
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-navy-900 hover:bg-navy-800 text-white text-lg px-8 py-6"
              asChild
            >
              <Link to="/contact">
                Contact Us <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white text-lg px-8 py-6"
              asChild
            >
              <Link to="/solutions">View Solutions</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Process;
